import { useMemo, useState } from "react"
import { Modal } from "components"
import axios from "axios"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { lang } from "config"

import SelectDescription from "components/forms/select-description"
import useBranchRepositories from "repositories/branch"

const Content = ({ onSuccess }) => {
  const [file, setFile] = useState(null)
  const [outletId, setOutletId] = useState("")
  const [errorMessages, setErrorMessages] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false })

  const outletOptions = useMemo(() => outletSelection || [], [outletSelection])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!outletId) {
      toast.error("Silakan pilih outlet terlebih dahulu")
      return
    }

    if (!file) {
      toast.error("Silakan pilih file Excel terlebih dahulu")
      return
    }

    const url = `${process.env.REACT_APP_INVENTORY_API_URL}/import-price`

    const formData = new FormData()
    formData.append("outlet_id", outletId)
    formData.append("file", file)

    nProgress.start()
    setSubmitting(true)

    try {
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      toast.success(response?.data?.message || "Import price berhasil diproses")

      setErrorMessages([])
      if (onSuccess) onSuccess()
    } catch (error) {
      const status = error?.response?.status

      if (status === 422) {
        const data = error.response.data
        const errs = Array.isArray(data?.errors)
          ? data.errors
          : typeof data?.errors === "object"
          ? Object.values(data.errors).flat()
          : [data?.message || "Validasi gagal."]

        setErrorMessages(errs.filter(Boolean))
        toast.error(data?.message || "Validasi gagal")
      } else {
        toast.error(error?.response?.data?.message || "Gagal meng-import price")
      }
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-6 text-xs">
      <div className="space-y-2">
        <p className="text-sm text-neutral-700">
          Upload file Excel dengan format:
        </p>
        <ul className="pl-5 space-y-1 list-disc text-[11px] text-neutral-600">
          <li>
            Header baris pertama minimal:
            <code className="px-1 mx-1 rounded bg-neutral-100">
              product_unit_id
            </code>{" "}
            (atau{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">legacy_name</code>{" "}
            sebagai fallback),
            <code className="px-1 mx-1 rounded bg-neutral-100">new_pcs_price</code>,
            <code className="px-1 mx-1 rounded bg-neutral-100">
              new_pcs_store_price
            </code>,<br></br>
            <code className="px-1 mx-1 rounded bg-neutral-100">new_lusin_price</code>
            &{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">
              new_lusin_store_price
            </code>
          </li>
          <li>
            Kalau{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">product_unit_id</code>{" "}
            kosong, importer resolve dari{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">legacy_name</code>{" "}
            via legacy mapping table.
          </li>
          <li>
            Kolom{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">name</code> &{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">
              current_pcs_price
            </code>{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">
              current_pcs_store_price
            </code>{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">
              current_lusin_price
            </code>{" "}
            <code className="px-1 mx-1 rounded bg-neutral-100">
              current_lusin_store_price
            </code>{" "}
            boleh ada (opsional) tapi tidak dipakai sebagai key.
          </li>
          <li>1 baris = 1 product unit. (new_pcs_price, new_pcs_store_price, new_lusin_price, new_lusin_store_price) harus angka ({">= 0"}).</li>
          <li>Jika (new_pcs_price, new_pcs_store_price) tidak diisi maka akan di hitung dengan rumus.</li>
          <li>Harga yang di-update akan disimpan dengan active_date = hari ini.</li>
        </ul>
      </div>

      {/* Outlet Select (sesuai contoh) */}
      <div className="space-y-2">
        <label className="block text-xs font-medium text-neutral-700">
          Outlet
        </label>
        <SelectDescription
          onChange={(value) => {
            setOutletId(value)
            setErrorMessages([])
          }}
          selection={outletOptions}
          isLoading={isLoadingOutletSelection}
          value={outletId}
          keyValue={(value) => value.id}
          title={(value) => value.name}
          description={(value) => value.code || value.id}
          placeholder="Select Outlet"
        />
        {outletId && (
          <p className="text-[11px] text-neutral-500">
            Selected outlet:{" "}
            <span className="font-medium">
              {outletOptions.find((o) => o.id === outletId)?.name || outletId}
            </span>
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-neutral-700">
          File Excel
        </label>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => {
            const f = e.target.files?.[0]
            setFile(f || null)
            setErrorMessages([])
          }}
          className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-neutral-800 file:text-white hover:file:bg-neutral-900"
        />
        {file && (
          <p className="text-[11px] text-neutral-500">
            Selected file: <span className="font-medium">{file.name}</span>
          </p>
        )}
      </div>

      {errorMessages.length > 0 && (
        <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
          <div className="font-semibold mb-1">{lang?.error || "Error"}:</div>
          <ul className="pl-4 space-y-1 list-disc">
            {errorMessages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <button
          type="submit"
          disabled={submitting}
          className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90 disabled:opacity-60"
        >
          <span>{submitting ? "Meng-upload..." : "Import Excel Price"}</span>
        </button>
      </div>
    </form>
  )
}

const ImportPrice = ({ isOpen, onClose, onSuccess }) => {
  const handleSuccess = () => {
    onClose?.()
    onSuccess?.()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Product Unit Price"
      content={<Content onSuccess={handleSuccess} />}
    />
  )
}

export default ImportPrice
